// Printable monthly cleaning chart: one A4 landscape page, the sheet exactly as supplied.
// Plain print layout: no dropdowns, buttons, "Today" label or colours. The Checked By and
// Supervised By columns are left blank for handwritten signatures.
const SESSIONS = [
  { key: 'm', label: 'Morning' },
  { key: 'd', label: 'Mid Day' },
  { key: 'e', label: 'Evening' },
];
const ITEMS = [
  { key: 'handwash', label: 'Handwash' },
  { key: 'hygiene', label: 'Hygiene' },
  { key: 'basin', label: 'Wash basin cleaned' },
  { key: 'damage', label: 'Damage / Leakage' },
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PAGE = { width: 297, height: 210 }; // A4 landscape, mm
const MARGIN = { left: 10, right: 10, top: 10, bottom: 8 };

const formatDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${SHORT_MONTHS[m - 1]} ${y}`;
};

export const cleaningPdfFileName = (chart) => {
  const safe = (text) => String(text).replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return `Cleaning_Chart_${safe(chart.washroomName) || 'Washroom'}_${MONTHS[chart.month - 1]}_${chart.year}.pdf`;
};

export async function downloadCleaningChartPdf(chart) {
  // Loaded on demand so the PDF libraries are not part of the main bundle.
  const [{ jsPDF }, autoTableModule] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const autoTable = autoTableModule.default || autoTableModule.autoTable;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const centre = PAGE.width / 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('ESOFT METRO CAMPUS – ROUTINE TOILET CLEANING & INSPECTION', centre, MARGIN.top + 5, { align: 'center' });
  doc.setFontSize(10);
  doc.text('Daily Routine Monitoring Sheet', centre, MARGIN.top + 11, { align: 'center' });

  // "Label: value" pairs, the value placed after the measured width of the label.
  const labelled = (label, value, x) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, x, MARGIN.top + 19);
    const labelWidth = doc.getTextWidth(label); // measured in bold, before the font changes
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), x + labelWidth + 2, MARGIN.top + 19);
  };
  doc.setFontSize(10);
  labelled('Washroom:', chart.washroomName, MARGIN.left);
  const monthText = `${MONTHS[chart.month - 1]} ${chart.year}`;
  doc.setFont('helvetica', 'bold');
  const monthLabelWidth = doc.getTextWidth('Month:') + 2;
  doc.setFont('helvetica', 'normal');
  labelled('Month:', monthText, PAGE.width - MARGIN.right - monthLabelWidth - doc.getTextWidth(monthText));

  const startY = MARGIN.top + 23;
  const headHeight = 17; // the two header rows, with wrapped labels
  const rowHeight = Math.min(5.6, (PAGE.height - startY - MARGIN.bottom - headHeight) / chart.days.length);

  const head = [
    [
      { content: 'Date', rowSpan: 2 },
      ...SESSIONS.map((s) => ({ content: s.label, colSpan: ITEMS.length })),
      { content: 'Checked By', rowSpan: 2 },
      { content: 'Supervised By', rowSpan: 2 },
    ],
    SESSIONS.flatMap(() => ITEMS.map((i) => i.label)),
  ];

  const body = chart.days.map((day) => [
    formatDate(day.date),
    ...SESSIONS.flatMap((s) => ITEMS.map((i) => ({ content: '', checked: Boolean(day.checks[`${s.key}_${i.key}`]) }))),
    // Left empty on purpose: these two are signed by hand on the printed sheet.
    '',
    '',
  ]);

  const tickWidth = 14;
  const dateWidth = 24;
  const nameWidth = (PAGE.width - MARGIN.left - MARGIN.right - dateWidth - tickWidth * SESSIONS.length * ITEMS.length) / 2;
  const columnStyles = { 0: { cellWidth: dateWidth, halign: 'left' } };
  for (let c = 1; c <= SESSIONS.length * ITEMS.length; c += 1) columnStyles[c] = { cellWidth: tickWidth };
  columnStyles[SESSIONS.length * ITEMS.length + 1] = { cellWidth: nameWidth, halign: 'left' };
  columnStyles[SESSIONS.length * ITEMS.length + 2] = { cellWidth: nameWidth, halign: 'left' };

  autoTable(doc, {
    head,
    body,
    startY,
    theme: 'grid',
    margin: MARGIN,
    styles: { font: 'helvetica', fontSize: 7, cellPadding: { top: 0.6, bottom: 0.6, left: 1.2, right: 1.2 }, lineColor: [0, 0, 0], lineWidth: 0.2, textColor: [0, 0, 0], valign: 'middle', minCellHeight: rowHeight, overflow: 'ellipsize' },
    headStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center', fontSize: 6.5, minCellHeight: 6, overflow: 'linebreak' },
    columnStyles,
    // Tick boxes are drawn as shapes: the built-in PDF fonts have no check-mark character.
    didDrawCell: (data) => {
      if (data.section !== 'body' || data.column.index < 1 || data.column.index > SESSIONS.length * ITEMS.length) return;
      const size = Math.min(2.8, data.cell.height - 1.2);
      const x = data.cell.x + (data.cell.width - size) / 2;
      const y = data.cell.y + (data.cell.height - size) / 2;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.rect(x, y, size, size);
      if (data.cell.raw && data.cell.raw.checked) {
        doc.setLineWidth(0.45);
        doc.line(x + size * 0.18, y + size * 0.55, x + size * 0.42, y + size * 0.82);
        doc.line(x + size * 0.42, y + size * 0.82, x + size * 0.86, y + size * 0.16);
      }
    },
  });

  doc.save(cleaningPdfFileName(chart));
}
